import { Component, Input, OnInit, Optional } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { PrintService } from '../../print.service';
import { SummaryService } from '../../services/summary/summary.service';

/** Riga compatta dell'elenco servizi dell'Estratto conto. */
interface EcServiceRow {
  count?: number;
  date: string;
  detail: string;
  tag: string;
  title: string;
}

/**
 * Stampa dell'Estratto conto dell'accesso agenzia. Replica il layout della bozza
 * approvata: logo TO + dati Agenzia, elenco servizi compatto e dettaglio quote.
 */
@Component({
  selector: 'app-print-ec',
  styleUrls: ['./print-ec.component.scss'],
  templateUrl: './print-ec.component.html'
})
export class PrintEcComponent implements OnInit {
  @Input() public data: any;
  @Input() public isModal = true;

  public advInformations: any;
  public allDays: any;
  public amountApis: any;
  public commissionType = '';
  public extra: any;
  public feeList: any[] = [];
  public insuranceList: any[] = [];
  public iter: any;
  public logoAdv = '';
  public logoTourOperator = '';
  public passengerNames = '';
  public quote: any = {};
  public services: EcServiceRow[] = [];
  public tenant: any;
  public totalPax = 0;

  constructor(
    @Optional() public activeModal: NgbActiveModal,
    private readonly printService: PrintService,
    private readonly summary: SummaryService
  ) { }

  public ngOnInit(): void {
    if (this.data) {
      this.iter = this.data.iter;
      this.allDays = this.data.allDays;
      this.extra = this.data.extra || { fees: [], insurances: [] };
      this.logoAdv = this.data.logoAdv;
      this.logoTourOperator = this.data.logoTourOperator;
      this.tenant = this.data.tenant;
      this.amountApis = this.data.amountApis || {};
      this.advInformations = this.data.advInformations || {};
      this.commissionType = this.iter?.commission_type || '';
    }

    // Inietta il CSS di stampa del tenant anche nell'anteprima on-screen: i default
    // del componente restano un fallback quando il tenant non configura printStyleUrl.
    this.loadPrintStyleUrl();
    this.totalPax = this.computeTotalPax();
    this.passengerNames = this.computePassengerNames();
    this.services = this.buildServices();
    this.feeList = this.amountApis?.fees?.list || [];
    this.insuranceList = this.amountApis?.insurances?.list || [];
    this.quote = this.buildQuote();
  }

  // Carica il CSS di stampa del tenant (printSettings.printStyleUrl) nel DOM: vale sia per
  // l'anteprima (view) sia per la stampa (il PDF lo riceve a parte). Le classi EC (.ec-page,
  // .svc-table, .tag, .quote-table, …) sono le stesse della bozza, quindi il CSS personalizzato
  // del cliente può sovrascrivere i colori.
  private loadPrintStyleUrl(): void {
    const url = this.tenant?.printSettings?.printStyleUrl;
    if (!url) { return; }

    const existing = document.querySelector(`link[href="${url}"]`);
    if (existing) { return; }

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = url;
    document.head.appendChild(link);
  }

  public getAbsoluteTourOperatorLogo(): string {
    if (!this.logoTourOperator) {
      return '';
    }
    if (this.logoTourOperator.startsWith('http://') || this.logoTourOperator.startsWith('https://')) {
      return this.logoTourOperator;
    }
    const cleanPath = this.logoTourOperator.startsWith('/') ? this.logoTourOperator : '/' + this.logoTourOperator;

    return window.location.origin + cleanPath;
  }

  public onLogoError(event: any): void {
    if (event?.target) {
      (event.target as HTMLImageElement).style.display = 'none';
    }
  }

  public close(): void {
    if (this.activeModal) {
      this.activeModal.close('Closed');
    }
  }

  public async print(): Promise<void> {
    try {
      const element = document.getElementById('modal-print-content');
      if (!element) {
        throw new Error('Elemento di stampa non trovato');
      }
      await this.printService.generatePDF(element, this.resolveCustomCssUrl());
    } catch (error) {
      alert('Si è verificato un errore durante la generazione del PDF. Riprova tra qualche istante.');
    }
  }

  private resolveCustomCssUrl(): string {
    if (this.tenant?.printSettings?.printStyleUrl) {
      return this.tenant.printSettings.printStyleUrl;
    }
    if (this.tenant?.consolidator) {
      return `assets/custom/${this.tenant.consolidator}/css/print.css`;
    }

    return '';
  }

  private computeTotalPax(): number {
    if (!this.iter?.rooms) {
      return 0;
    }
    let total = 0;
    for (const room of this.iter.rooms) {
      total += (room.adults || 0);
      if (room.childrenBirthdate) {
        total += room.childrenBirthdate.length;
      }
    }

    return total;
  }

  private computePassengerNames(): string {
    const passengers = this.iter?.passengers || [];

    return passengers
      .map((passenger: any) => [passenger.title, passenger.name, passenger.surname].filter(Boolean).join(' ').trim())
      .filter((name: string) => !!name)
      .join(', ');
  }

  private buildServices(): EcServiceRow[] {
    const rows: EcServiceRow[] = [];
    const days = this.data?.days || [];

    for (const day of days) {
      (day?.flights || []).forEach((flight: any) => rows.push(this.flightRow(flight)));
      (day?.hotels || []).forEach((hotel: any) => rows.push(this.hotelRow(hotel)));
      (day?.cars || []).forEach((car: any) => rows.push(this.carRow(car)));
      (day?.transfers || []).forEach((transfer: any) => this.pushOptionals(rows, transfer, 'Transfer', day?.date));
      (day?.escorteds || []).forEach((escorted: any) => this.pushOptionals(rows, escorted, 'Tour', day?.date));
      (day?.optionals || []).forEach((optional: any) => this.pushOptionals(rows, optional, 'Optional', day?.date));
    }

    const extras: EcServiceRow[] = [];
    (this.extra?.fees || []).forEach((fee: any) => extras.push({
      date: this.formatDate(fee.date),
      detail: fee.description || '',
      tag: 'Fee',
      title: fee.name || ''
    }));
    (this.extra?.insurances || []).forEach((insurance: any) => extras.push({
      date: this.formatDate(insurance.date),
      detail: insurance.description || '',
      tag: 'Fee',
      title: insurance.name || ''
    }));
    rows.push(...this.groupExtras(extras));

    return rows.filter((row: EcServiceRow) => !!row.title);
  }

  /** Aggrega gli extra identici (stesso tag/titolo/dettaglio) in un'unica riga con `count`. */
  private groupExtras(extras: EcServiceRow[]): EcServiceRow[] {
    const grouped = new Map<string, EcServiceRow>();
    for (const row of extras) {
      const key = `${row.tag}|${row.title}|${row.detail}`;
      const existing = grouped.get(key);
      if (existing) {
        existing.count = (existing.count || 1) + 1;
      } else {
        grouped.set(key, { ...row, count: 1 });
      }
    }

    return Array.from(grouped.values());
  }

  private pushOptionals(rows: EcServiceRow[], container: any, tag: string, fallbackDate: string): void {
    (container?.optionals || []).forEach((optional: any) => {
      const title = optional?.type || optional?.name || '';
      const itemName = optional?.items?.[0]?.name || optional?.name || '';
      rows.push({
        date: this.serviceDate(optional, fallbackDate),
        detail: itemName && itemName !== title ? itemName : '',
        tag,
        title
      });
    });
  }

  private flightRow(flight: any): EcServiceRow {
    const segments = flight?.segments || [];
    const first = segments[0];
    const last = segments[segments.length - 1];
    const title = first && last
      ? `${first.departure?.airportName || ''} → ${last.arrival?.airportName || ''}`
      : (flight?.name || '');
    const detail = this.flightDetail(first);
    const date = first ? `${this.formatDate(first.departure?.date)} ${first.departure?.hour || ''}`.trim() : '';

    return { date, detail, tag: 'Volo', title };
  }

  private flightDetail(segment: any): string {
    if (!segment) {
      return '';
    }
    const parts: string[] = [];
    const airline = segment.marketingAirline?.code || segment.operatingAirline?.code || '';
    const number = `${airline}${segment.flightNumber ? ' ' + segment.flightNumber : ''}`.trim();
    if (number) {
      parts.push(number);
    }
    if (segment.classFlight) {
      parts.push(segment.classFlight);
    }
    const pieces = segment.baggageInfo?.[0]?.Pieces;
    if (pieces) {
      parts.push(`${pieces} ${String(pieces) === '1' ? 'bagaglio' : 'bagagli'}`);
    }

    return parts.join(' · ');
  }

  private hotelRow(hotel: any): EcServiceRow {
    const first = hotel?.hotels?.[0] || {};
    const room = first?.rooms?.[0] || {};
    const detail = [first.city_name, room.room_description, room.plan_description]
      .filter((part: string) => !!part)
      .join(' · ');
    const date = this.formatRange(hotel?.checkin, hotel?.checkout);

    return { date, detail, tag: 'Hotel', title: first.name || '' };
  }

  private carRow(car: any): EcServiceRow {
    const vehicle = car?.vehicles?.[0] || {};
    const detail = [vehicle.vendor?.name, vehicle.carClass]
      .filter((part: string) => !!part)
      .join(' · ');
    const date = this.formatRange(car?.pickup?.date, car?.dropoff?.date);

    return { date, detail, tag: 'Auto', title: vehicle.model || '' };
  }

  private serviceDate(optional: any, fallbackDate: string): string {
    const steps = optional?.steps || [];
    if (steps.length > 0 && steps[0].dateFrom) {
      return this.formatRange(steps[0].dateFrom, steps[steps.length - 1].dateTo);
    }
    if (optional?.date) {
      return this.formatDate(optional.date);
    }

    return this.formatDate(fallbackDate);
  }

  private formatDate(value: string): string {
    const parts = this.splitDate(value);
    if (!parts) {
      return '';
    }
    const [year, month, day] = parts;

    return `${day}.${month}.${year}`;
  }

  private formatRange(from: string, to: string): string {
    const start = this.splitDate(from);
    const end = this.splitDate(to);
    if (!start && !end) {
      return '';
    }
    if (!end || from === to) {
      return start ? `${start[2]}.${start[1]}.${start[0]}` : '';
    }
    if (!start) {
      return `${end[2]}.${end[1]}.${end[0]}`;
    }

    return `${start[2]}.${start[1]} → ${end[2]}.${end[1]}`;
  }

  private splitDate(value: string): string[] | null {
    if (!value) {
      return null;
    }
    const date = String(value).substring(0, 10);
    const parts = date.split('-');
    if (parts.length !== 3) {
      return null;
    }

    return parts;
  }

  private buildQuote(): any {
    const ap = this.amountApis || {};
    const adv = this.advInformations || {};
    const feeLand = Number(adv.feeLandServices) || 0;
    const feeFlight = Number(adv.feeFlight) || 0;
    const feeFlightTaxes = Number(adv.feeFlightTaxes) || 0;
    const commission = Number(ap.commissionOrDiscount) || 0;
    const withholding = Number(ap.withholdingTax) || 0;
    const totals = this.summary.calculateTotals(this.normalizeAllDays(), this.extra || { fees: [], insurances: [] });

    return {
      commission,
      flightTaxes: (Number(ap.flightTaxesPrice) || 0) + feeFlightTaxes,
      flightTickets: (Number(ap.flightTicketsPrice) || 0) + feeFlight,
      hasWithholdingTax: withholding !== 0,
      landServices: (Number(ap.baseTripPrice) || 0) + feeLand,
      optionServices: (totals.optionalsSuggestedTotal || 0) + (totals.transfersSuggestedTotal || 0),
      preventivo: (Number(ap.totalPrice) || 0) + feeFlight + feeFlightTaxes + feeLand,
      total: (Number(ap.totalPrice) || 0) + withholding - commission,
      withholding
    };
  }

  private normalizeAllDays(): any {
    const source = this.allDays || {};

    return {
      cars: source.cars || [],
      escorteds: source.escorteds || [],
      flights: source.flights || [],
      hotels: source.hotels || [],
      optionals: source.optionals || [],
      optionalsSuggested: source.optionalsSuggested || [],
      transfers: source.transfers || [],
      transfersSuggested: source.transfersSuggested || []
    };
  }
}
